import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Search, Download, Phone, Mail, Calendar, ShoppingBag, X, MapPin, MessageSquare, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR');
};
const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR');
};

const ClientsTab = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/clients`);
        setClients(r.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API}/clients/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `clients_beni_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export error', e);
    }
  };

  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) ||
           (c.email || '').toLowerCase().includes(s) ||
           (c.phone || '').toLowerCase().includes(s);
  });

  const totalReservations = clients.reduce((s, c) => s + (c.reservations_count || 0), 0);
  const totalOrders = clients.reduce((s, c) => s + (c.orders_count || 0), 0);
  const totalRevenue = clients.reduce((s, c) => s + (c.total_spent || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" /></div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4" data-testid="admin-clients-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-white text-lg font-medium flex items-center gap-2">
          <Users className="w-5 h-5 text-[#d4af37]" /> Clients <span className="text-[#d4af37] text-sm">({clients.length})</span>
        </h2>
        <button
          onClick={exportCsv}
          className="flex items-center gap-2 bg-[#d4af37] text-black px-4 py-2 text-xs uppercase tracking-wider hover:bg-white transition rounded"
          data-testid="clients-export-btn"
        >
          <Download className="w-4 h-4" /> Exporter CSV
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#121212] border border-white/10 p-3 rounded text-center">
          <p className="text-2xl font-bold text-white">{clients.length}</p>
          <p className="text-white/40 text-[10px] uppercase tracking-wider">Clients</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-3 rounded text-center">
          <p className="text-2xl font-bold text-blue-400">{totalReservations}</p>
          <p className="text-white/40 text-[10px] uppercase tracking-wider">Réservations</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-3 rounded text-center">
          <p className="text-2xl font-bold text-[#d4af37]">{totalOrders}</p>
          <p className="text-white/40 text-[10px] uppercase tracking-wider">Commandes</p>
        </div>
        <div className="bg-[#121212] border border-white/10 p-3 rounded text-center">
          <p className="text-2xl font-bold text-green-400">{totalRevenue.toFixed(0)}€</p>
          <p className="text-white/40 text-[10px] uppercase tracking-wider">Total Livraison</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par nom, email ou téléphone..."
          className="bg-[#121212] border-white/20 text-white pl-10"
          data-testid="clients-search"
        />
      </div>

      {/* Table */}
      <div className="bg-[#121212] border border-white/10 rounded overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black/40 border-b border-white/10">
              <tr>
                <th className="text-left p-3 text-white/60 text-xs uppercase tracking-wider">Nom</th>
                <th className="text-left p-3 text-white/60 text-xs uppercase tracking-wider hidden md:table-cell">Contact</th>
                <th className="text-center p-3 text-white/60 text-xs uppercase tracking-wider">Rés.</th>
                <th className="text-center p-3 text-white/60 text-xs uppercase tracking-wider">Cmd.</th>
                <th className="text-right p-3 text-white/60 text-xs uppercase tracking-wider hidden sm:table-cell">Total</th>
                <th className="text-right p-3 text-white/60 text-xs uppercase tracking-wider hidden lg:table-cell">Dernière activité</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-white/30">Aucun client</td>
                </tr>
              ) : filtered.map(c => (
                <tr
                  key={c.key}
                  onClick={() => setSelected(c)}
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition"
                  data-testid={`client-row-${c.key}`}
                >
                  <td className="p-3">
                    <p className="text-white font-medium">{c.name || '—'}</p>
                    <p className="text-white/40 text-xs md:hidden">{c.phone || c.email}</p>
                  </td>
                  <td className="p-3 text-white/60 text-xs hidden md:table-cell">
                    {c.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div>}
                    {c.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>}
                  </td>
                  <td className="p-3 text-center">
                    {c.reservations_count > 0 ? <span className="text-blue-400 font-medium">{c.reservations_count}</span> : <span className="text-white/20">—</span>}
                  </td>
                  <td className="p-3 text-center">
                    {c.orders_count > 0 ? <span className="text-[#d4af37] font-medium">{c.orders_count}</span> : <span className="text-white/20">—</span>}
                  </td>
                  <td className="p-3 text-right hidden sm:table-cell">
                    {c.total_spent > 0 ? <span className="text-green-400 font-medium">{c.total_spent.toFixed(2)}€</span> : <span className="text-white/20">—</span>}
                  </td>
                  <td className="p-3 text-right text-white/50 text-xs hidden lg:table-cell">{formatDate(c.last_activity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {selected && <ClientDetailModal client={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </motion.div>
  );
};

const ClientDetailModal = ({ client, onClose }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-end md:items-center justify-center p-0 md:p-4"
      data-testid="client-detail-modal"
    >
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0d0d0d] border border-white/10 w-full md:max-w-2xl md:rounded-lg rounded-t-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0d0d0d] p-4 border-b border-white/10 flex items-center justify-between gap-3 border-l-4 border-l-[#d4af37]">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-white/40">Profil Client</p>
            <h2 className="text-white text-xl font-semibold truncate">{client.name || 'Client'}</h2>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-2 -mr-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-black/30 p-3 rounded text-center">
              <p className="text-blue-400 text-2xl font-bold">{client.reservations_count || 0}</p>
              <p className="text-white/40 text-[10px] uppercase">Réservations</p>
            </div>
            <div className="bg-black/30 p-3 rounded text-center">
              <p className="text-[#d4af37] text-2xl font-bold">{client.orders_count || 0}</p>
              <p className="text-white/40 text-[10px] uppercase">Commandes</p>
            </div>
            <div className="bg-black/30 p-3 rounded text-center">
              <p className="text-green-400 text-2xl font-bold">{(client.total_spent || 0).toFixed(0)}€</p>
              <p className="text-white/40 text-[10px] uppercase">Total dépensé</p>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-2 bg-black/30 p-3 rounded">
            {client.email && (
              <a href={`mailto:${client.email}`} className="flex items-center gap-2 text-white hover:text-[#d4af37] transition break-all">
                <Mail className="w-4 h-4 text-[#d4af37] shrink-0" />
                <span>{client.email}</span>
              </a>
            )}
            {client.phone && (
              <a href={`tel:${client.phone}`} className="flex items-center gap-2 text-white hover:text-[#d4af37] transition">
                <Phone className="w-4 h-4 text-[#d4af37] shrink-0" />
                <span>{client.phone}</span>
              </a>
            )}
          </div>

          {/* Reservations history */}
          {client.reservations?.length > 0 && (
            <div>
              <h3 className="text-blue-400 text-xs uppercase tracking-wider mb-2 flex items-center gap-2"><Calendar className="w-4 h-4" /> Réservations ({client.reservations.length})</h3>
              <div className="space-y-2">
                {client.reservations.slice(0, 20).map((r, i) => (
                  <div key={i} className="bg-black/30 p-3 rounded border-l-2 border-blue-400/40">
                    <div className="flex items-center justify-between text-sm flex-wrap gap-2">
                      <div>
                        <span className="text-white font-medium">{r.date}</span> <span className="text-[#d4af37]">{r.time}</span> <span className="text-white/50">— {r.guests} pers.</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded ${
                        r.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                        r.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                        'bg-[#d4af37]/20 text-[#d4af37]'
                      }`}>
                        {r.status === 'confirmed' ? 'Confirmée' : r.status === 'cancelled' ? 'Annulée' : 'En attente'}
                      </span>
                    </div>
                    {r.notes && (
                      <p className="text-yellow-400/80 text-xs italic mt-1 flex items-start gap-1">
                        <MessageSquare className="w-3 h-3 shrink-0 mt-0.5" /> {r.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Orders history */}
          {client.orders?.length > 0 && (
            <div>
              <h3 className="text-[#d4af37] text-xs uppercase tracking-wider mb-2 flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> Commandes livraison ({client.orders.length})</h3>
              <div className="space-y-2">
                {client.orders.slice(0, 20).map((o, i) => (
                  <div key={i} className="bg-black/30 p-3 rounded border-l-2 border-[#d4af37]/40">
                    <div className="flex items-center justify-between text-sm flex-wrap gap-2">
                      <span className="text-white/60 text-xs">{formatDateTime(o.created_at)}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded ${
                          o.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                          o.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>{o.status}</span>
                        <span className="text-[#d4af37] font-bold">{o.total?.toFixed(2)}€</span>
                      </div>
                    </div>
                    <div className="text-white/60 text-xs mt-1">
                      {o.items?.map((it, j) => (
                        <div key={j}>
                          <span className="text-[#d4af37]">{it.quantity}×</span> {it.name_fr}
                          {it.notes && <span className="text-yellow-400/70 italic"> · {it.notes}</span>}
                        </div>
                      ))}
                    </div>
                    {o.address && (
                      <p className="text-white/40 text-[10px] mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {o.address}
                      </p>
                    )}
                    {o.notes && (
                      <p className="text-yellow-400/80 text-xs italic mt-1 flex items-start gap-1">
                        <MessageSquare className="w-3 h-3 shrink-0 mt-0.5" /> {o.notes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ClientsTab;
